import {
  ChangeDetectionStrategy,
  Component,
  booleanAttribute,
  computed,
  input,
  output,
} from '@angular/core';

import { Icon } from '../icon/icon';
import { IconName } from '../icon/icon-name';

export type ButtonVariant = 'primary' | 'secondary' | 'text';
export type ButtonSize = 'default' | 'sm';
export type ButtonType = 'button' | 'submit' | 'reset';

/**
 * Design-system Button (figma 25:132). Three variants:
 *
 * - `primary`   — action fill (black/white flips with theme), raised.
 * - `secondary` — card surface, raised, action-ink label.
 * - `text`      — chromeless 24px text button; takes its accent colour on hover.
 *
 * `destructive` recolours each variant (danger fill on primary, danger text on
 * secondary/text). Sizes: default 48px, sm 32px (text is always 24px).
 * Label comes from the `label` input or projected content.
 *
 *   <app-button variant="secondary" iconLeft="icon-export" (activated)="export()">
 *     {{ t('common.export') }}
 *   </app-button>
 */
@Component({
  selector: 'app-button',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [Icon],
  template: `
    <button
      class="btn"
      [class]="variantClass()"
      [class.sm]="size() === 'sm'"
      [class.destructive]="destructive()"
      [type]="type()"
      [disabled]="disabled()"
      [attr.aria-label]="ariaLabel()"
      (click)="activated.emit($event)"
    >
      @if (iconLeft(); as icon) {
        <app-icon [name]="icon" [size]="24" />
      }
      <span class="label">{{ label() }}<ng-content /></span>
      @if (iconRight(); as icon) {
        <app-icon [name]="icon" [size]="24" />
      }
    </button>
  `,
  styles: `
    /* Host stretches with layout utilities (w-full / grid col-span) — the
       native button fills whatever width the host is given. */
    :host {
      display: inline-flex;
    }
    .btn {
      flex: 1;
      min-width: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      height: 48px;
      padding: 0 16px;
      border: 0;
      border-radius: var(--radius-control);
      font:
        var(--font-weight-semibold) var(--text-button) / var(--text-button--line-height)
        var(--font-body);
      white-space: nowrap;
      transition:
        background-color 0.15s ease,
        box-shadow 0.15s ease,
        color 0.15s ease;
    }
    .btn:disabled {
      opacity: 0.5;
    }
    .sm {
      height: 32px;
    }
    .label {
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .label:empty {
      display: none;
    }

    /* Primary — action fill; hover inverts to the white card surface (per the
       designers), downstate presses the white with the 10% overlay. */
    .primary {
      background: var(--action-fill);
      color: var(--action-text);
      box-shadow: var(--shadow-raised);
    }
    .primary:hover:not(:disabled):not(:active) {
      background: var(--surface-card);
      color: var(--action-fill);
      box-shadow:
        0 0 0 1px var(--border-strong),
        0 0 2px 0 var(--core-overlay-200),
        0 2px 8px 0 var(--core-overlay-200);
    }
    .primary:active:not(:disabled) {
      background:
        linear-gradient(var(--overlay-down-1), var(--overlay-down-1)),
        linear-gradient(var(--surface-card), var(--surface-card));
      color: var(--action-fill);
      box-shadow: var(--shadow-hairline);
    }
    .primary:disabled {
      box-shadow: var(--shadow-hairline);
    }

    /* Primary destructive — danger fill, black-alpha overlays on hover/downstate.
       Disabled falls back to the plain primary disabled look (per spec). */
    .primary.destructive:not(:disabled) {
      background: var(--colorscales-red-500);
    }
    .primary.destructive:hover:not(:disabled):not(:active) {
      background:
        linear-gradient(var(--overlay-down-1), var(--overlay-down-1)),
        linear-gradient(var(--colorscales-red-500), var(--colorscales-red-500));
      box-shadow:
        inset 0 0 0 1px var(--core-overlay-200),
        0 0 2px 0 var(--core-overlay-200),
        0 2px 8px 0 var(--core-overlay-200);
    }
    .primary.destructive:active:not(:disabled) {
      background:
        linear-gradient(var(--overlay-down-2), var(--overlay-down-2)),
        linear-gradient(var(--colorscales-red-500), var(--colorscales-red-500));
      box-shadow: var(--shadow-hairline);
    }

    /* Secondary — card surface + action-ink label; hover adds a strong ring. */
    .secondary {
      background: var(--surface-card);
      color: var(--action-fill);
      box-shadow: var(--shadow-raised);
    }
    .secondary:hover:not(:disabled):not(:active) {
      box-shadow:
        0 0 0 1px var(--border-strong),
        var(--shadow-raised);
    }
    .secondary:active:not(:disabled) {
      background: var(--core-overlay-200);
      box-shadow: var(--shadow-hairline);
    }
    .secondary:disabled {
      box-shadow: var(--shadow-hairline);
    }
    .secondary.destructive {
      color: var(--text-red);
    }

    /* Text button — chromeless, ink at rest, accent colour on hover/press. */
    .text {
      height: 24px;
      padding: 0 8px;
      background: transparent;
      color: var(--action-fill);
      box-shadow: none;
    }
    .text:hover:not(:disabled),
    .text:active:not(:disabled) {
      color: var(--text-interactive-hover);
    }
    .text.destructive:hover:not(:disabled),
    .text.destructive:active:not(:disabled) {
      color: var(--text-red);
    }
  `,
})
export class Button {
  readonly variant = input<ButtonVariant>('primary');
  readonly size = input<ButtonSize>('default');
  readonly destructive = input(false, { transform: booleanAttribute });
  readonly disabled = input(false, { transform: booleanAttribute });
  readonly type = input<ButtonType>('button');
  readonly iconLeft = input<IconName | null>(null);
  readonly iconRight = input<IconName | null>(null);
  /** Label text; alternatively project content into the element. */
  readonly label = input<string>('');
  /** For icon-only usage — sets aria-label on the native button. */
  readonly ariaLabel = input<string | null>(null);

  /** Emitted on click (native click also bubbles; use either). */
  readonly activated = output<MouseEvent>();

  protected readonly variantClass = computed(() => this.variant());
}
